import { useEffect, useState } from "react";
import { collection, onSnapshot, query, where } from "firebase/firestore";
import { db } from "../firebase";
import { S, C } from "../ui";

function tsToDateKey(ts) {
  if (!ts) return "";
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toISOString().slice(0, 10);
}
function tsToLabel(ts) {
  if (!ts) return "";
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
}

// "Historical who-was-responsible" (spec Phase 4): resolved via
// VendorContract's own snapshotted vendorName (see FacilityAdminDashboard's
// addVendor), not a live join to vendors/{id} — so a report covering a
// period where a vendor was later removed still shows the right name.
// "date" is the event's own date key (YYYY-MM-DD), so a vendor swap
// mid-report period attributes each event to whoever was actually
// responsible on that specific day.
function resolveVendorName(vendorId, dateKey, contracts, vendorsById) {
  if (!vendorId) return "Internal Staff";
  const active = contracts.find(c => c.vendorId === vendorId && c.startDate <= dateKey && (!c.endDate || dateKey <= c.endDate));
  if (active?.vendorName) return active.vendorName;
  const any = contracts.find(c => c.vendorId === vendorId);
  if (any?.vendorName) return any.vendorName;
  return vendorsById[vendorId]?.companyName || "Unknown vendor";
}

// vendorId: null (Facility Admin) reports across every vendor + internal
// staff. A real vendorId (Vendor Admin) reports only their own activity —
// "a vendor pulls their own scorecard history to attach to a bid at a
// different building" (spec Phase 4).
export default function ReportPanel({ facilityId, vendorId, facilityName }) {
  const [completions, setCompletions] = useState([]);
  const [timeCards, setTimeCards] = useState([]);
  const [incidents, setIncidents] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [vendors, setVendors] = useState([]);
  const today = new Date().toISOString().slice(0, 10);
  const monthAgo = new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10);
  const [startDate, setStartDate] = useState(monthAgo);
  const [endDate, setEndDate] = useState(today);
  const [generated, setGenerated] = useState(false);

  const filterField = vendorId ? "vendorId" : "facilityId";
  const filterValue = vendorId || facilityId;

  useEffect(() => onSnapshot(query(collection(db, "checklistCompletions"), where(filterField, "==", filterValue)), s =>
    setCompletions(s.docs.map(d => d.data()))), [filterField, filterValue]);
  useEffect(() => onSnapshot(query(collection(db, "timeCardEntries"), where(filterField, "==", filterValue)), s =>
    setTimeCards(s.docs.map(d => d.data()))), [filterField, filterValue]);
  useEffect(() => onSnapshot(query(collection(db, "incidentFlags"), where(filterField, "==", filterValue)), s =>
    setIncidents(s.docs.map(d => d.data()))), [filterField, filterValue]);
  useEffect(() => onSnapshot(query(collection(db, "vendorContracts"), where("facilityId", "==", facilityId)), s =>
    setContracts(s.docs.map(d => d.data()))), [facilityId]);
  useEffect(() => {
    if (vendorId) return; // Vendor Admin doesn't need the full roster
    return onSnapshot(query(collection(db, "vendors"), where("facilityId", "==", facilityId)), s =>
      setVendors(s.docs.map(d => ({ id: d.id, ...d.data() }))));
  }, [facilityId, vendorId]);
  const vendorsById = Object.fromEntries(vendors.map(v => [v.id, v]));

  const inRange = (dateKey) => dateKey >= startDate && dateKey <= endDate;
  const eventsInRange = [
    ...completions.map(c => ({ kind: "Checklist", dateKey: tsToDateKey(c.timestamp), when: tsToLabel(c.timestamp), vendorId: c.vendorId, detail: `${c.items?.filter(i => i.done).length ?? 0}/${c.items?.length ?? 0} items completed`, photos: [...(c.photoUrls?.before || []), ...(c.photoUrls?.after || [])] })),
    ...timeCards.map(t => ({ kind: "Time Card", dateKey: tsToDateKey(t.clockInAt), when: tsToLabel(t.clockInAt), vendorId: t.vendorId, detail: t.status === "open" ? "Clocked in, not yet clocked out" : `Clocked in → out (${tsToLabel(t.clockOutAt)})`, photos: [t.clockInPhotoUrl, t.clockOutPhotoUrl].filter(Boolean) })),
    ...incidents.map(i => ({ kind: "Incident", dateKey: tsToDateKey(i.timestamp), when: tsToLabel(i.timestamp), vendorId: i.vendorId, detail: `${i.type}: ${i.description}${i.resolved ? " (resolved)" : " (open)"}`, photos: [] })),
  ].filter(e => inRange(e.dateKey)).sort((a, b) => a.dateKey.localeCompare(b.dateKey));

  return (
    <div>
      <div className="report-controls" style={{ display: "flex", gap: 10, alignItems: "flex-end", marginBottom: 20, flexWrap: "wrap" }}>
        <div>
          <div style={{ fontSize: 11, color: C.muted, marginBottom: 4 }}>Start date</div>
          <input type="date" style={{ ...S.input, marginBottom: 0 }} value={startDate} onChange={e => setStartDate(e.target.value)} />
        </div>
        <div>
          <div style={{ fontSize: 11, color: C.muted, marginBottom: 4 }}>End date</div>
          <input type="date" style={{ ...S.input, marginBottom: 0 }} value={endDate} onChange={e => setEndDate(e.target.value)} />
        </div>
        <button style={{ ...S.btn(), width: "auto", padding: "10px 18px" }} onClick={() => setGenerated(true)}>Generate Report</button>
        {generated && <button style={{ ...S.ghostBtn, width: "auto", padding: "10px 18px" }} onClick={() => window.print()}>Print / Save as PDF</button>}
      </div>

      {generated && (
        <div className="report-output" style={{ background: C.white, borderRadius: 0, border: `1px solid ${C.border}`, padding: 24 }}>
          <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 2 }}>{vendorId ? "Your Activity Report" : "Facility Compliance Report"}</div>
          <div style={{ fontSize: 13, color: C.muted, marginBottom: 20 }}>{facilityName} · {startDate} to {endDate} · {eventsInRange.length} events</div>

          {eventsInRange.length === 0 && <div style={{ fontSize: 13, color: C.muted }}>No activity in this date range.</div>}

          {eventsInRange.map((e, idx) => (
            <div key={idx} style={{ padding: "12px 0", borderBottom: idx === eventsInRange.length - 1 ? "none" : `1px solid ${C.border}` }}>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}>
                <div>
                  <span style={S.pill(C.tintAccent, C.accent)}>{e.kind}</span>{" "}
                  <b>{resolveVendorName(e.vendorId, e.dateKey, contracts, vendorsById)}</b> — {e.detail}
                </div>
                <div style={{ color: C.muted }}>{e.when}</div>
              </div>
              {e.photos.length > 0 && (
                <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                  {e.photos.map((url, i) => <img key={i} src={url} alt="" style={{ width: 64, height: 64, objectFit: "cover", borderRadius: 0, border: `1px solid ${C.border}` }} />)}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <style>{`
        @media print {
          body * { visibility: hidden; }
          .report-output, .report-output * { visibility: visible; }
          .report-output { position: absolute; top: 0; left: 0; width: 100%; border: none !important; }
          .report-controls { display: none !important; }
        }
      `}</style>
    </div>
  );
}
